/**
 * THE CASE-TYPE VOCABULARY, AND THE THREE DIALECTS THAT WRITE IT.
 *
 * Measured on staging 2026-08-26:
 *
 *   LISTINGS (~272k stored values) speak a short Title Case:
 *     OB 54,511 · General 38,228 · Ortho 33,400 · Cardiac 26,767 ·
 *     Pediatric 22,159 · Regional 21,681 · GI 18,217 · Neuro 16,758 ·
 *     Trauma 14,324 · Vascular 14,086 · Thoracic 8,391 · Pain 4,616
 *   EMPLOYERS picked from eight lowercase slugs that between them could not say
 *     OB, Trauma, Vascular, Thoracic or Neuro -- five of the twelve, including
 *     the largest case type in the collection.
 *   PROVIDERS state a long form: Obstetric Anesthesia 61 · Neuroanesthesia 53 ·
 *     Pediatric Anesthesia 43 · Regional Anesthesia 42 · Neuraxial 15 ·
 *     Neonatal 6
 *
 * Canonicalisation happens when COMPARING and when RENDERING, never on the
 * stored bytes, so widening the list orphans nothing.
 *
 * EXACT TWIN of backend/src/common/utils/case-type.util.ts. Both specs carry
 * the same cases so a change to one fails the other.
 */

/** Canonical values. APPEND-ONLY: the first eight are frozen. */
export const CASE_TYPE_VALUES = [
  'general',
  'pediatric',
  'cardiac',
  'ortho',
  'regional',
  'pain',
  'critical-care',
  'gi-endoscopy',
  'obstetrics',
  'neuro',
  'trauma',
  'vascular',
  'thoracic',
  'ophthalmology',
];

/**
 * Display labels, authored rather than derived. Deriving them is what produced
 * 'Ob' and 'Gi Endoscopy' from a split-on-hyphen title-caser. 'OB' because that
 * is what anesthesia says and what 54,511 stored rows already say.
 */
export const CASE_TYPE_LABELS = {
  general: 'General',
  pediatric: 'Pediatric',
  cardiac: 'Cardiac',
  ortho: 'Ortho',
  regional: 'Regional',
  pain: 'Pain',
  'critical-care': 'Critical Care',
  'gi-endoscopy': 'GI Endoscopy',
  obstetrics: 'OB',
  neuro: 'Neuro',
  trauma: 'Trauma',
  vascular: 'Vascular',
  thoracic: 'Thoracic',
  ophthalmology: 'Ophthalmology',
};

const norm = (value) =>
  String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const CASE_SYNONYMS = {
  ob: 'obstetrics',
  obs: 'obstetrics',
  obgyn: 'obstetrics',
  obstetric: 'obstetrics',
  obstetrical: 'obstetrics',
  obstetricanesthesia: 'obstetrics',
  obanesthesia: 'obstetrics',
  laboranddelivery: 'obstetrics',
  ld: 'obstetrics',
  gi: 'gi-endoscopy',
  giendoscopy: 'gi-endoscopy',
  endoscopy: 'gi-endoscopy',
  endo: 'gi-endoscopy',
  gastroenterology: 'gi-endoscopy',
  orthopedic: 'ortho',
  orthopedics: 'ortho',
  orthopaedic: 'ortho',
  orthopaedics: 'ortho',
  peds: 'pediatric',
  pediatrics: 'pediatric',
  paediatric: 'pediatric',
  pediatricanesthesia: 'pediatric',
  // A sub-set of the same deck, and six provider profiles hold it while no
  // listing does -- leaving it unmapped would match those providers to nothing.
  neonatal: 'pediatric',
  cardiacanesthesia: 'cardiac',
  cardiovascular: 'cardiac',
  cardiothoracic: 'cardiac',
  heart: 'cardiac',
  hearts: 'cardiac',
  regionalanesthesia: 'regional',
  // A regional technique, not neurosurgery. Folding it the other way would put
  // 15 provider profiles on the wrong deck.
  neuraxial: 'regional',
  blocks: 'regional',
  neuroanesthesia: 'neuro',
  neurosurgery: 'neuro',
  neurosurgical: 'neuro',
  neurological: 'neuro',
  painmanagement: 'pain',
  interventionalpain: 'pain',
  chronicpain: 'pain',
  criticalcare: 'critical-care',
  icu: 'critical-care',
  intensivecare: 'critical-care',
  generalsurgery: 'general',
  endovascular: 'vascular',
  ophthalmic: 'ophthalmology',
  eye: 'ophthalmology',
  ophtho: 'ophthalmology',
};

/** The canonical slug a value means, or null when it is outside the vocabulary. */
export function canonicalCaseType(value) {
  const n = norm(value);
  if (!n) return null;
  const exact = CASE_TYPE_VALUES.find((c) => norm(c) === n);
  if (exact) return exact;
  return CASE_SYNONYMS[n] ?? null;
}

/** The words to show a human for a stored value, canonical or not. */
export function caseTypeLabel(value) {
  const canonical = canonicalCaseType(value);
  return canonical ? CASE_TYPE_LABELS[canonical] : String(value ?? '');
}

/**
 * A stored list, canonicalised and de-duplicated.
 *
 * A value outside the vocabulary is KEPT AS TYPED -- dropping it is precisely
 * the silent deletion this exists to stop.
 */
export function normalizeCaseTypes(values) {
  if (!Array.isArray(values)) return [];
  const out = [];
  for (const v of values) {
    const canonical = canonicalCaseType(v);
    const kept = canonical ?? (v == null ? '' : String(v));
    if (kept && !out.includes(kept)) out.push(kept);
  }
  return out;
}

/**
 * Every canonical case type as a picker option, in vocabulary order. Areas of
 * proficiency ARE case types (services spec 2026-09-19, 4.1), so all four
 * proficiency pickers offer exactly this list. Frontend only: the backend twin
 * has no picker, so this is not part of the twin contract above.
 */
export const CASE_TYPE_OPTIONS = CASE_TYPE_VALUES.map((value) => ({
  value,
  label: CASE_TYPE_LABELS[value],
}));

/**
 * A stored proficiency list as a picker reads it. `selected` holds canonical
 * slugs in stored order, de-duplicated (a stored 'Neuraxial' preselects
 * Regional). `unknown` holds the values outside the vocabulary KEPT AS TYPED:
 * the picker shows them as extra selected chips and saves them back untouched.
 */
export function proficiencySelection(stored) {
  const selected = [];
  const unknown = [];
  (Array.isArray(stored) ? stored : []).forEach((value) => {
    const canonical = canonicalCaseType(value);
    if (canonical) {
      if (!selected.includes(canonical)) selected.push(canonical);
      return;
    }
    const typed = value == null ? '' : String(value);
    if (typed.trim() && !unknown.includes(typed)) unknown.push(typed);
  });
  return { selected, unknown };
}

/**
 * What a proficiency picker saves: the canonical LABEL for each selected slug
 * (so a stored 'Neuraxial' is written back as 'Regional' on the next save),
 * then the kept values as typed. There is no data migration; this is the only
 * rewrite, and it happens only when the provider saves.
 */
export function proficiencyStorage(selectedSlugs, unknown) {
  const labels = [];
  (Array.isArray(selectedSlugs) ? selectedSlugs : []).forEach((slug) => {
    const canonical = canonicalCaseType(slug);
    const label = canonical ? CASE_TYPE_LABELS[canonical] : null;
    if (label && !labels.includes(label)) labels.push(label);
  });
  const kept = [];
  (Array.isArray(unknown) ? unknown : []).forEach((value) => {
    if (
      typeof value === 'string' &&
      value.trim() &&
      !labels.includes(value) &&
      !kept.includes(value)
    ) {
      kept.push(value);
    }
  });
  return [...labels, ...kept];
}

/**
 * The next `areasOfProficiency` array after a picker taps `value` -- a
 * canonical slug from CASE_TYPE_OPTIONS, or an unknown value kept as typed.
 * Already selected/kept -> removed; not yet present -> added. Shared by every
 * proficiency picker (the services Cases sheet and ClinicalProficiencySection)
 * so the add/remove/save rule lives in exactly one place.
 */
export function toggleProficiencyValue(stored, value) {
  const { selected, unknown } = proficiencySelection(stored);
  const nextSelected = unknown.includes(value)
    ? selected
    : selected.includes(value)
      ? selected.filter((slug) => slug !== value)
      : [...selected, value];
  return proficiencyStorage(nextSelected, unknown.filter((kept) => kept !== value));
}
